/**
 * The rules about changing a block, in one place (T52).
 *
 * ⚠️ **The server decides, and this is what it decides with.** The client runs
 * the same function only so that it can grey out an impossible edit before
 * sending it; if the two ever disagree, the server is right by definition
 * (`Development/CLAUDE.md` #6).
 *
 * Everything here is pure arithmetic over numbers the caller looks up, so it
 * tests without a world, a socket or a database.
 */

import { BLOCK_AIR } from './blocks.ts';
import type { MessageKey } from './i18n.ts';
import { MAX_REACH } from './raycast.ts';
import { WORLD_MAX_Y, WORLD_MIN_Y, inWorld } from './world.ts';

/**
 * Eye height above the block you are standing on, in metres.
 *
 * Shared rather than a client constant because reach is measured from the eye,
 * and a server measuring from the feet would refuse edits the player can
 * plainly see — 1.7 m of disagreement is a quarter of the whole reach.
 */
export const EYE_HEIGHT = 1.7;

/**
 * ⚠️ Slack on the reach check, in metres.
 *
 * `player_state` arrives a few times a second, so the position the server is
 * measuring from is always a little stale — and a flying player covers real
 * ground between updates. Without slack, building while moving would be refused
 * at random, which reads as "block placing is unreliable" and is impossible to
 * reproduce on purpose.
 *
 * The reach limit exists to stop a **buggy** client corrupting a shared world
 * (D3), not to stop a cheat — everyone here is family (D1) — so erring generous
 * costs nothing that matters.
 */
export const REACH_TOLERANCE = 4;

/** Blocks that cannot be broken, by key. */
const INDESTRUCTIBLE: ReadonlySet<string> = new Set(['bedrock']);

export interface EditContext {
  /** Where the player's feet are, in world metres. */
  readonly player: { readonly x: number; readonly y: number; readonly z: number };
  /** The block currently at the target, as an id. */
  readonly current: number;
  /** The key of the block currently at the target, for the indestructible check. */
  readonly currentKey: string | undefined;
  /** What the player is trying to put there — `BLOCK_AIR` to break. */
  readonly next: number;
}

export type EditVerdict =
  { readonly ok: true } | { readonly ok: false; readonly reasonKey: MessageKey };

const OK: EditVerdict = { ok: true };

/**
 * May this edit happen?
 *
 * Reasons are written for a player to read, because they are shown on screen —
 * an edit that silently does nothing is indistinguishable from a broken game
 * (D2).
 *
 * 🔴 **A key, not a sentence (T124).** The refusal travels to the client
 * (protocol 8) and is translated there. Typing it as {@link MessageKey} means a
 * refusal reason that does not exist in the language file **fails the build**,
 * rather than showing the player a raw key at the one moment they are already
 * confused about why nothing happened.
 */
/**
 * Can the player standing at `player` touch the block at `x, y, z`?
 *
 * 🔴 **Pulled out of {@link validateEdit} so that `use_block` measures reach
 * with the SAME arithmetic** (T129, protocol 12). Picking a berry is not an
 * edit — no block changes and nothing is broadcast — but it is still a thing
 * done to the world from a distance, and reach is the rule that bounds
 * everything done to the world. ⚠️ **A second distance check written beside
 * this one would eventually disagree with it**, and the version that let you
 * pick berries from across the valley is the one nobody would notice.
 */
export function withinReach(
  x: number,
  y: number,
  z: number,
  player: { readonly x: number; readonly y: number; readonly z: number },
): boolean {
  const eyeY = player.y + EYE_HEIGHT;
  /* Centre of the block, not its corner: measuring to the corner makes the
     reach a metre shorter in some directions than others, for no reason a
     player could ever work out. */
  const distance = Math.hypot(x + 0.5 - player.x, y + 0.5 - eyeY, z + 0.5 - player.z);
  return distance <= MAX_REACH + REACH_TOLERANCE;
}

export function validateEdit(x: number, y: number, z: number, ctx: EditContext): EditVerdict {
  if (y < WORLD_MIN_Y || y > WORLD_MAX_Y) {
    return { ok: false, reasonKey: 'edit.rejected.outsideWorld' };
  }

  /* ⚠️ The world still has an edge — it is just Iceland's now, not the test
     area's (T193, D113). Allowing an edit outside it would write a delta for a
     chunk nobody can ever be sent, and beyond the DEM there is no ground to
     stand on because there is no height data at all.

     🔴 **This USED to be `TEST_AREA`**, which is why the world was 1,400 ×
     1,150 m. `TEST_AREA` still exists and still means something — where we
     started — but it stopped being the answer to *where can you build*. */
  if (!inWorld(x, z)) {
    return { ok: false, reasonKey: 'edit.rejected.outsideWorld' };
  }

  /**
   * ⚠️ **Reach is checked before anything about the target block.**
   *
   * The order matters and the first version had it wrong. Answering "there is
   * already something there" for a coordinate 500 m away tells the sender what
   * the world contains at a place they cannot see — and, more practically, it
   * means the rule that actually bounds an edit is the last one consulted
   * rather than the first.
   */
  if (!withinReach(x, y, z, ctx.player)) {
    return { ok: false, reasonKey: 'edit.rejected.tooFar' };
  }

  if (ctx.next === ctx.current) {
    return { ok: false, reasonKey: 'edit.rejected.nothingToChange' };
  }

  if (ctx.next === BLOCK_AIR) {
    if (ctx.current === BLOCK_AIR) return { ok: false, reasonKey: 'edit.rejected.nothingToBreak' };
    if (ctx.currentKey !== undefined && INDESTRUCTIBLE.has(ctx.currentKey)) {
      return { ok: false, reasonKey: 'edit.rejected.indestructible' };
    }
  } else if (ctx.current !== BLOCK_AIR) {
    /* ⚠️ Placing only into air, deliberately. Replacing whatever is there
       looks harmless until somebody paves over a building by mis-clicking. */
    return { ok: false, reasonKey: 'edit.rejected.occupied' };
  }

  if (ctx.next !== BLOCK_AIR && occupiedByPlayer(x, y, z, ctx.player)) {
    return { ok: false, reasonKey: 'edit.rejected.standingThere' };
  }

  return OK;
}

/**
 * Is this block one of the two the player is standing in?
 *
 * Feet and head. Placing into either would leave you inside a solid block,
 * which in a world with no suffocation damage simply means stuck — and the way
 * out is not obvious.
 */
function occupiedByPlayer(
  x: number,
  y: number,
  z: number,
  player: { readonly x: number; readonly y: number; readonly z: number },
): boolean {
  const px = Math.floor(player.x);
  const pz = Math.floor(player.z);
  if (x !== px || z !== pz) return false;
  const feet = Math.floor(player.y);
  return y === feet || y === feet + 1;
}
